/**
 * ConnectAPIPanel — OkBot Connect API
 * Documentación y panel de gestión de la API pública que las plataformas
 * externas usan para que OkBot sea su cerebro (usuarios + OkWallet + Agente IA).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Code2, Copy, CheckCircle2, Globe, Users, Wallet, Bot,
  RefreshCw, Key, Link2, ArrowRight, Zap, Shield,
  ChevronDown, ChevronUp, ExternalLink, Terminal,
  BookOpen, Activity, TrendingUp, Database, Send,
  BarChart3, AlertCircle, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax  = (path, opts = {}) => axios({ url: `${API}${path}`, ...opts });

function CopyBtn({ text, label = '' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy}
      className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-white transition-colors">
      {copied ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copiado' : (label || 'Copiar')}
    </button>
  );
}

function CodeBlock({ code, lang = 'http', title = '' }) {
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 text-xs">
      {title && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-white/5 border-b border-white/10">
          <span className="text-gray-400 font-mono">{title}</span>
          <CopyBtn text={code} />
        </div>
      )}
      <div className="bg-black/40 p-3 overflow-x-auto">
        <pre className="text-gray-300 font-mono whitespace-pre-wrap leading-relaxed">{code}</pre>
      </div>
    </div>
  );
}

function EndpointRow({ method, path, description, expanded, onToggle, children }) {
  const METHOD_COLOR = {
    GET:    'bg-green-500/20 text-green-300 border-green-500/30',
    POST:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
    PUT:    'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    DELETE: 'bg-red-500/20 text-red-300 border-red-500/30',
  };
  return (
    <div className="border border-white/8 rounded-xl overflow-hidden">
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-white/3 transition-colors text-left">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border flex-shrink-0 ${METHOD_COLOR[method] || 'bg-gray-500/20 text-gray-300'}`}>
          {method}
        </span>
        <span className="font-mono text-xs text-gray-200 flex-1">{path}</span>
        <span className="text-gray-500 text-xs hidden md:block">{description}</span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                  : <ChevronDown className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
      </button>
      {expanded && <div className="border-t border-white/8 p-4 bg-white/2">{children}</div>}
    </div>
  );
}

const BASE = typeof window !== 'undefined'
  ? `${window.location.protocol}//${window.location.hostname}`
  : 'https://tu-okbot.com';

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Introducción
// ══════════════════════════════════════════════════════════════════════════════
function TabIntro({ platforms }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const STEPS = [
    {
      icon: Link2, color: 'text-blue-400', bg: 'bg-blue-500/10',
      step: '1', title: 'Vincula tu plataforma en OkBot',
      desc: 'Ve a "Plataformas Conectadas" en Super Admin Connect. OkBot te dará un Platform Code y una API Key.'
    },
    {
      icon: Terminal, color: 'text-green-400', bg: 'bg-green-500/10',
      step: '2', title: 'Llama a /api/connect/register',
      desc: 'Desde tu plataforma, haz un POST a OkBot con tu Platform Code y Key. OkBot activará todos los servicios.'
    },
    {
      icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10',
      step: '3', title: 'Envía usuarios a OkBot',
      desc: 'Cada vez que se registre un usuario en tu plataforma, notifica a OkBot. Él los almacena y propaga.'
    },
    {
      icon: Wallet, color: 'text-yellow-400', bg: 'bg-yellow-500/10',
      step: '4', title: 'Usa OkWallet como wallet central',
      desc: 'Todas las operaciones de wallet (recarga, débito, transferencia) las procesa OkBot centralmente.'
    },
  ];

  return (
    <div className="space-y-5">
      {/* Hero */}
      <div className="glass-panel p-5 border border-red-500/20 bg-gradient-to-r from-red-500/5 to-purple-500/5">
        <h3 className="text-white font-bold text-base mb-1.5 flex items-center gap-2">
          <Code2 className="w-5 h-5 text-red-400" />OkBot Connect API
        </h3>
        <p className="text-gray-400 text-sm">
          La API pública que cualquier plataforma externa puede usar para declarar a OkBot como su cerebro central.
          Una vez configurada, OkBot gestiona <strong className="text-white">usuarios, wallets y agente IA</strong> de forma centralizada para todas las plataformas vinculadas.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="text-xs bg-white/5 border border-white/10 px-2.5 py-1 rounded-lg text-gray-300">
            Base URL: <code className="text-red-300">{BASE}/api/connect</code>
          </span>
          <span className="text-xs bg-white/5 border border-white/10 px-2.5 py-1 rounded-lg text-gray-300">
            Auth: Headers <code className="text-blue-300">X-Platform-Code</code> + <code className="text-blue-300">X-Platform-Key</code>
          </span>
        </div>
      </div>

      {/* Cómo empezar */}
      <div className="glass-panel p-4">
        <h4 className="text-sm font-semibold text-gray-200 mb-4">¿Cómo empezar?</h4>
        <div className="grid md:grid-cols-2 gap-3">
          {STEPS.map(({ icon: Icon, color, bg, step, title, desc }) => (
            <div key={step} className={`p-3.5 rounded-xl border border-white/8 ${bg}`}>
              <div className="flex items-start gap-3">
                <div className={`w-7 h-7 rounded-lg ${bg} border border-white/10 flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-xs font-bold ${color}`}>{step}</span>
                </div>
                <div>
                  <p className={`text-sm font-semibold ${color} mb-0.5`}>{title}</p>
                  <p className="text-gray-500 text-xs">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plataformas vinculadas y sus credenciales */}
      {platforms?.length > 0 && (
        <div className="glass-panel p-4">
          <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <Key className="w-4 h-4 text-yellow-400" />Credenciales por plataforma vinculada
          </h4>
          <div className="space-y-2">
            {platforms.map(p => (
              <div key={p.id} className="bg-white/3 border border-white/8 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${p.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
                  <span className="text-white text-xs font-mono font-semibold">{p.platform_code}</span>
                  <span className="text-gray-500 text-xs truncate">{p.platform_url}</span>
                </div>
                <CodeBlock
                  title="Headers para autenticación"
                  code={`X-Platform-Code: ${p.platform_code}\nX-Platform-Key: [la API key de tu plataforma]`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ejemplo completo de registro */}
      <div className="glass-panel p-4">
        <h4 className="text-sm font-semibold text-gray-200 mb-3">Ejemplo completo: Registrar plataforma</h4>
        <CodeBlock
          title="POST /api/connect/register"
          code={`curl -X POST ${BASE}/api/connect/register \\
  -H "Content-Type: application/json" \\
  -H "X-Platform-Code: TU_PLATFORM_CODE" \\
  -H "X-Platform-Key: TU_API_KEY" \\
  -d '{
    "platform_name": "Mi Plataforma",
    "platform_url": "https://mi-plataforma.com",
    "callback_url": "https://mi-plataforma.com/api/okbot-webhooks",
    "features": ["users", "wallet", "agent"]
  }'`}
        />
        <div className="mt-3">
          <CodeBlock
            title="Respuesta exitosa"
            code={`{
  "success": true,
  "message": "OkBot está activo como cerebro de tu plataforma",
  "okbot_services": {
    "users": true,
    "wallet": true,
    "agent": true,
    "sync": true
  }
}`}
          />
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Usuarios API
// ══════════════════════════════════════════════════════════════════════════════
function TabUsersAPI() {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const endpoints = [
    {
      method: 'POST', path: '/api/connect/users',
      description: 'Registra o actualiza un usuario en OkBot',
      example: `curl -X POST ${BASE}/api/connect/users \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "usuario@ejemplo.com",
    "name": "Juan Pérez",
    "phone": "+52 55 1234 5678",
    "role": "user",
    "status": "active",
    "remote_id": "123"
  }'`,
      response: `{
  "success": true,
  "action": "created",
  "master_id": "uuid-del-usuario-maestro",
  "email": "usuario@ejemplo.com",
  "okbot_linked": true
}`,
      notes: 'OkBot almacena el usuario y lo propaga automáticamente a todas las demás plataformas vinculadas.'
    },
    {
      method: 'GET', path: '/api/connect/users/{email}',
      description: 'Consulta el perfil maestro de un usuario',
      example: `curl ${BASE}/api/connect/users/usuario@ejemplo.com \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key"`,
      response: `{
  "email": "usuario@ejemplo.com",
  "name": "Juan Pérez",
  "unified_role": "user",
  "platforms": ["OKXXXX", "OKYYYYY"],
  "platform_count": 2,
  "wallet_balance": 250.00,
  "wallet_currency": "MXN"
}`,
      notes: 'Devuelve el perfil unificado incluyendo en qué plataformas existe y su balance de OkWallet.'
    },
    {
      method: 'GET', path: '/api/connect/users',
      description: 'Lista todos tus usuarios almacenados en OkBot',
      example: `curl "${BASE}/api/connect/users?page=1&limit=50" \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key"`,
      response: `{
  "total": 342,
  "page": 1,
  "pages": 7,
  "users": [...]
}`,
    },
    {
      method: 'POST', path: '/api/connect/sync/push-users',
      description: 'Envía lista masiva de usuarios a OkBot',
      example: `curl -X POST ${BASE}/api/connect/sync/push-users \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"users": [{"email":"a@x.com","name":"Ana"},{"email":"b@x.com","name":"Bob"}]}'`,
      response: `{"success": true, "created": 2, "updated": 0, "skipped": 0}`,
      notes: 'Ideal para sincronización inicial de toda tu base de usuarios con OkBot.'
    },
  ];

  return (
    <div className="space-y-3">
      <div className="glass-panel p-3 border border-blue-500/20 bg-blue-500/5 flex gap-2">
        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-blue-300 text-xs">
          Cuando registras un usuario en OkBot, él lo gestiona como registro maestro y lo propaga a todas las plataformas vinculadas. <strong>No necesitas sincronizar manualmente entre plataformas.</strong>
        </p>
      </div>
      {endpoints.map((ep, i) => (
        <EndpointRow key={i} method={ep.method} path={ep.path}
          description={ep.description}
          expanded={!!expanded[i]} onToggle={() => toggle(i)}>
          <div className="space-y-3">
            {ep.notes && (
              <p className="text-gray-400 text-xs italic border-l-2 border-blue-500/40 pl-3">{ep.notes}</p>
            )}
            <CodeBlock title="Ejemplo cURL" code={ep.example} />
            <CodeBlock title="Respuesta" code={ep.response} />
          </div>
        </EndpointRow>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: OkWallet API
// ══════════════════════════════════════════════════════════════════════════════
function TabWalletAPI() {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const endpoints = [
    {
      method: 'GET', path: '/api/connect/wallet/balance/{email}',
      description: 'Consulta el balance central de un usuario',
      example: `curl ${BASE}/api/connect/wallet/balance/usuario@ejemplo.com \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key"`,
      response: `{
  "email": "usuario@ejemplo.com",
  "balance": 1250.00,
  "currency": "MXN",
  "total_recharged": 2000.00,
  "total_debited": 750.00,
  "last_transaction": "2026-08-05T01:30:00Z",
  "recent_transactions": [...]
}`,
      notes: 'El balance es único y centralizado en OkBot. Todas las plataformas ven el mismo saldo.'
    },
    {
      method: 'POST', path: '/api/connect/wallet/recharge',
      description: 'Registra una recarga en OkWallet',
      example: `curl -X POST ${BASE}/api/connect/wallet/recharge \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "usuario@ejemplo.com",
    "amount": 500.00,
    "description": "Recarga desde plataforma A",
    "reference": "REF-2026-001",
    "currency": "MXN"
  }'`,
      response: `{
  "success": true,
  "transaction_id": "uuid",
  "email": "usuario@ejemplo.com",
  "amount": 500.00,
  "new_balance": 1750.00,
  "currency": "MXN"
}`,
      notes: 'OkBot acredita el monto y el nuevo balance es visible inmediatamente en todas las plataformas.'
    },
    {
      method: 'POST', path: '/api/connect/wallet/debit',
      description: 'Registra un débito/cobro en OkWallet',
      example: `curl -X POST ${BASE}/api/connect/wallet/debit \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "usuario@ejemplo.com",
    "amount": 150.00,
    "description": "Cobro de servicio premium",
    "reference": "COBRO-001"
  }'`,
      response: `{
  "success": true,
  "transaction_id": "uuid",
  "amount": 150.00,
  "new_balance": 1100.00
}`,
      notes: 'OkBot verifica el saldo antes de autorizar. Si no hay fondos, devuelve error 400 "Saldo insuficiente".'
    },
    {
      method: 'POST', path: '/api/connect/wallet/transfer',
      description: 'Transfiere saldo entre usuarios (cross-platform)',
      example: `curl -X POST ${BASE}/api/connect/wallet/transfer \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from_email": "usuario1@ejemplo.com",
    "to_email": "usuario2@ejemplo.com",
    "amount": 200.00,
    "description": "Transferencia P2P"
  }'`,
      response: `{
  "success": true,
  "transaction_id": "uuid",
  "from_new_balance": 900.00,
  "to_new_balance": 700.00
}`,
      notes: 'Las transferencias funcionan entre usuarios de cualquier plataforma conectada. OkBot valida fondos y aplica en tiempo real.'
    },
    {
      method: 'GET', path: '/api/connect/wallet/history/{email}',
      description: 'Historial completo de transacciones',
      example: `curl "${BASE}/api/connect/wallet/history/usuario@ejemplo.com?limit=20" \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key"`,
      response: `{
  "email": "usuario@ejemplo.com",
  "current_balance": 1100.00,
  "total_transactions": 15,
  "transactions": [
    {"type": "recharge", "amount": 500.00, "platform": "OKXXXX", ...},
    {"type": "debit", "amount": 150.00, "platform": "OKXXXX", ...}
  ]
}`,
    },
    {
      method: 'GET', path: '/api/connect/wallet/summary',
      description: 'Resumen de todas las wallets de tu plataforma',
      example: `curl ${BASE}/api/connect/wallet/summary \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key"`,
      response: `{
  "platform_code": "OKXXXX",
  "users_with_wallet": 89,
  "total_balance_in_okwallet": 45200.00,
  "total_recharged": 80000.00,
  "total_debited": 34800.00,
  "currency": "MXN"
}`,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="glass-panel p-3 border border-yellow-500/20 bg-yellow-500/5 flex gap-2">
        <Wallet className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
        <p className="text-yellow-300 text-xs">
          <strong>OkWallet centralizado:</strong> el balance de cada usuario es único en OkBot. Sin importar desde qué plataforma se recarga o se cobra, el saldo es siempre el mismo. Las transferencias funcionan entre usuarios de distintas plataformas.
        </p>
      </div>
      {endpoints.map((ep, i) => (
        <EndpointRow key={i} method={ep.method} path={ep.path}
          description={ep.description}
          expanded={!!expanded[i]} onToggle={() => toggle(i)}>
          <div className="space-y-3">
            {ep.notes && (
              <p className="text-gray-400 text-xs italic border-l-2 border-yellow-500/40 pl-3">{ep.notes}</p>
            )}
            <CodeBlock title="Ejemplo cURL" code={ep.example} />
            <CodeBlock title="Respuesta" code={ep.response} />
          </div>
        </EndpointRow>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Agente IA API
// ══════════════════════════════════════════════════════════════════════════════
function TabAgentAPI() {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const endpoints = [
    {
      method: 'POST', path: '/api/connect/agent/install',
      description: 'Instala OkBot como agente IA principal',
      example: `curl -X POST ${BASE}/api/connect/agent/install \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agent_name": "OkBot",
    "priority": 1,
    "capabilities": ["natural_language","user_management","wallet_ops"],
    "callback_url": "https://mi-plataforma.com/okbot-callback"
  }'`,
      response: `{
  "success": true,
  "agent_id": "uuid",
  "status": "active",
  "priority": 1,
  "okbot_endpoint": "/api/connect/agent/query",
  "message": "OkBot está activo como agente IA principal de tu plataforma"
}`,
      notes: 'Una vez instalado, OkBot queda registrado como agente de prioridad 1. Los demás agentes de tu plataforma quedan como secundarios.'
    },
    {
      method: 'GET', path: '/api/connect/agent/status',
      description: 'Verifica que OkBot esté activo como agente',
      example: `curl ${BASE}/api/connect/agent/status \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key"`,
      response: `{
  "installed": true,
  "status": "active",
  "priority": 1,
  "capabilities": ["natural_language","user_management","wallet_ops"],
  "last_heartbeat": "2026-08-05T01:30:00Z"
}`,
    },
    {
      method: 'POST', path: '/api/connect/agent/query',
      description: 'Envía un mensaje al agente OkBot',
      example: `curl -X POST ${BASE}/api/connect/agent/query \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "¿Cuál es el saldo de wallet de este usuario?",
    "user_email": "usuario@ejemplo.com",
    "session_id": "session-abc-123"
  }'`,
      response: `{
  "agent": "OkBot",
  "user_context": {
    "name": "Juan Pérez",
    "role": "user",
    "platforms": ["OKXXXX", "OKYYYYY"]
  },
  "wallet_context": {
    "balance": 1100.00,
    "currency": "MXN"
  },
  "okbot_api_url": "/api/chat"
}`,
      notes: 'OkBot responde con el contexto maestro del usuario. Para chat completo de IA en tiempo real, usa /api/chat con JWT del usuario.'
    },
    {
      method: 'POST', path: '/api/connect/sync/push-events',
      description: 'Notifica eventos a OkBot en tiempo real',
      example: `curl -X POST ${BASE}/api/connect/sync/push-events \\
  -H "X-Platform-Code: OKXXXX" \\
  -H "X-Platform-Key: tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": [
      {"event": "user.created", "user": {"email":"nuevo@ejemplo.com","name":"Nuevo"}},
      {"event": "wallet.recharge", "data": {"email":"usuario@ejemplo.com","amount": 100}}
    ]
  }'`,
      response: `{"success": true, "processed": 2}`,
      notes: 'Úsalo como webhook saliente desde tu plataforma. OkBot procesa user.created, user.updated, wallet.recharge, wallet.debit, order.created automáticamente.'
    },
  ];

  return (
    <div className="space-y-3">
      <div className="glass-panel p-3 border border-purple-500/20 bg-purple-500/5 flex gap-2">
        <Bot className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
        <p className="text-purple-300 text-xs">
          <strong>OkBot como agente IA:</strong> al instalarlo en tu plataforma, queda registrado con prioridad 1. Los agentes existentes quedan como secundarios. OkBot tiene contexto completo de todos los usuarios y wallets de tu ecosistema.
        </p>
      </div>
      {endpoints.map((ep, i) => (
        <EndpointRow key={i} method={ep.method} path={ep.path}
          description={ep.description}
          expanded={!!expanded[i]} onToggle={() => toggle(i)}>
          <div className="space-y-3">
            {ep.notes && (
              <p className="text-gray-400 text-xs italic border-l-2 border-purple-500/40 pl-3">{ep.notes}</p>
            )}
            <CodeBlock title="Ejemplo cURL" code={ep.example} />
            <CodeBlock title="Respuesta" code={ep.response} />
          </div>
        </EndpointRow>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Estado de Plataformas
// ══════════════════════════════════════════════════════════════════════════════
function TabPlatformStatus({ platforms }) {
  const [statuses, setStatuses] = useState({});
  const [walletSummaries, setWalletSummaries] = useState({});
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Cargamos los datos del cerebro maestro que ya tenemos
      const r = await ax('/admin/connect/master/stats');
      const stats = r.data;
      // Construir status por plataforma desde la distribución
      const statusMap = {};
      for (const pd of (stats.platform_distribution || [])) {
        statusMap[pd.platform_code] = {
          users: pd.user_count,
          url: pd.url,
        };
      }
      setStatuses(statusMap);

      // Cargar brain registry
      const r2 = await ax('/admin/connect/master/registry');
      // enrich with wallet data from registry
    } catch(e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-200">Estado de cada plataforma como cerebro OkBot</h4>
        <button onClick={loadAll} disabled={loading}
          className="btn-secondary flex items-center gap-2 text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      <div className="space-y-3">
        {platforms?.map(p => {
          const st = statuses[p.platform_code] || {};
          const isBrain = p.okbot_brain_active;
          return (
            <div key={p.id} className={`glass-panel p-4 border ${isBrain ? 'border-green-500/20' : 'border-white/8'}`}>
              <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-xl ${isBrain ? 'bg-green-500/10' : 'bg-white/5'}`}>
                  <Brain className={`w-5 h-5 ${isBrain ? 'text-green-400' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-white font-mono font-semibold text-sm">{p.platform_code}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${
                      isBrain
                        ? 'text-green-400 bg-green-500/10 border-green-500/20'
                        : 'text-gray-500 bg-white/5 border-white/10'
                    }`}>
                      {isBrain ? '🧠 OkBot es el cerebro' : '⚠ No registrado aún'}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      p.status === 'active' ? 'text-green-400 bg-green-500/10' : 'text-gray-500'
                    }`}>{p.status}</span>
                  </div>
                  <p className="text-gray-500 text-xs mb-3 truncate">{p.platform_url}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    <div className="bg-white/3 rounded-lg p-2">
                      <p className="text-white font-bold">{st.users ?? '—'}</p>
                      <p className="text-gray-600 text-[10px]">Usuarios en OkBot</p>
                    </div>
                    <div className="bg-white/3 rounded-lg p-2">
                      <p className={`font-bold ${p.api_key_b64 ? 'text-green-400' : 'text-red-400'}`}>
                        {p.api_key_b64 ? '✓ API Key' : '✗ Sin key'}
                      </p>
                      <p className="text-gray-600 text-[10px]">Autenticación</p>
                    </div>
                    <div className="bg-white/3 rounded-lg p-2">
                      <p className={`font-bold ${p.oauth_sso_status === 'configured' ? 'text-purple-400' : 'text-gray-500'}`}>
                        {p.oauth_sso_status || 'No config'}
                      </p>
                      <p className="text-gray-600 text-[10px]">OAuth SSO</p>
                    </div>
                  </div>
                  {!isBrain && (
                    <div className="mt-3 p-2.5 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
                      <p className="text-yellow-400 text-[10px] font-semibold mb-1">
                        Para que esta plataforma reconozca a OkBot como su cerebro:
                      </p>
                      <CodeBlock
                        code={`POST ${BASE}/api/connect/register\nX-Platform-Code: ${p.platform_code}\nX-Platform-Key: [api_key]`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!platforms?.length && (
          <div className="glass-panel p-12 text-center">
            <Globe className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">No hay plataformas vinculadas aún</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Need the Brain import workaround for JSX
function Brain({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.44-4.66"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.44-4.66"/>
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: 'intro',    label: 'Introducción',    icon: BookOpen },
  { id: 'users',    label: 'Usuarios API',    icon: Users },
  { id: 'wallet',   label: 'OkWallet API',    icon: Wallet },
  { id: 'agent',    label: 'Agente IA API',   icon: Bot },
  { id: 'status',   label: 'Estado',          icon: Activity },
];

export default function ConnectAPIPanel({ platforms = [] }) {
  const [tab, setTab] = useState('intro');

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex flex-wrap gap-1.5">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              tab === id
                ? 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
        <a href={`${API.replace('/api','')}/api/connect/manifest`} target="_blank" rel="noreferrer"
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 transition-all">
          <ExternalLink className="w-3.5 h-3.5" />Manifest JSON
        </a>
      </div>

      {tab === 'intro'   && <TabIntro platforms={platforms} />}
      {tab === 'users'   && <TabUsersAPI />}
      {tab === 'wallet'  && <TabWalletAPI />}
      {tab === 'agent'   && <TabAgentAPI />}
      {tab === 'status'  && <TabPlatformStatus platforms={platforms} />}
    </div>
  );
}
